import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ProgressModal from '../components/ProgressModal';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function Articles() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [progressJobId, setProgressJobId] = useState(null);
  const [progressJobType, setProgressJobType] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchArticles = async () => {
    try {
      setLoading(true);
      // Fetch all pending articles (don't filter by score yet since they may not be scored)
      const response = await axios.get(`${API_BASE}/articles?status=pending`, {
        headers: getAuthHeaders()
      });
      // Show all pending articles (including unscored ones)
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (articleId) => {
    try {
      const response = await axios.put(`${API_BASE}/articles/${articleId}/approve`, {}, {
        headers: getAuthHeaders()
      });
      // Show progress modal for template generation
      if (response.data.jobId) {
        setProgressJobId(response.data.jobId);
        setProgressJobType('template-generation');
      }
      fetchArticles();
    } catch (error) {
      console.error('Error approving article:', error);
      alert('Error approving article');
    }
  };

  const handleReject = async (articleId) => {
    try {
      await axios.put(`${API_BASE}/articles/${articleId}/reject`, {}, {
        headers: getAuthHeaders()
      });
      fetchArticles();
    } catch (error) {
      console.error('Error rejecting article:', error);
      alert('Error rejecting article');
    }
  };

  const handleScrape = async () => {
    try {
      const response = await axios.post(
        `${API_BASE}/articles/scrape`,
        {},
        { headers: getAuthHeaders() }
      );
      // Show progress modal
      setProgressJobId(response.data.jobId);
      setProgressJobType('news-scraping');
    } catch (error) {
      console.error('Error triggering scrape:', error);
      if (error.response?.status === 401) {
        alert('Not logged in. Please refresh the page and log in again.');
      } else {
        alert(`Error: ${error.response?.data?.message || error.message || 'Unknown error'}`);
      }
    }
  };

  const handleProgressComplete = () => {
    setProgressJobId(null);
    setProgressJobType(null);
    // Refresh articles
    fetchArticles();
    setTimeout(() => fetchArticles(), 2000);
  };

  const handleProgressClose = () => {
    setProgressJobId(null);
    setProgressJobType(null);
  };

  return (
    <div>
      {progressJobId && (
        <ProgressModal
          jobId={progressJobId}
          jobType={progressJobType}
          onComplete={handleProgressComplete}
          onClose={handleProgressClose}
        />
      )}
      
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Articles</h1>
        <button
          onClick={handleScrape}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Scrape News
        </button>
      </div>

      {loading ? (
        <div className="text-center p-8">Loading articles...</div>
      ) : articles.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No articles pending review. Click "Scrape News" to fetch new articles.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {articles.map((article) => (
            <div key={article.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{article.title}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="capitalize">{article.source}</span>
                    <span>Score: {article.relevance_score}/100</span>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  article.relevance_score >= 90 ? 'bg-green-100 text-green-800' :
                  article.relevance_score >= 80 ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {article.relevance_score}
                </span>
              </div>

              <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                {article.content?.substring(0, 200)}...
              </p>

              {article.relevance_reasoning && (
                <div className="bg-gray-50 rounded p-3 mb-4">
                  <p className="text-xs text-gray-600">
                    <strong>AI Reasoning:</strong> {article.relevance_reasoning}
                  </p>
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => handleApprove(article.id)}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  Approve
                </button>
                <button
                  onClick={() => handleReject(article.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Reject
                </button>
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  View
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
